/* @jsxImportSource @emotion/react */
import React from 'react'
import { FormattedMessage } from 'react-intl'
import type { Components } from 'react-markdown'

import { CustomNoteIcon, Markdown } from '@slite/components'

import { helpArticlePathParams } from '../../data/helpCenterFormat'
import type { HelpAskSource } from '../../data/sliteHelpCenter'
import { useRoutes } from '../../routes'

// The public ask API pre-renders each citation as a `{[N](url)}` link; drop the
// wrapping braces (and the API's separating space/comma) so they render as
// inline reference pills instead of literal braces.
const CITATION_RE = /\s*,?\s*\{(\[\d+\]\([^)]+\))\}/g
const unwrapCitations = (markdown: string) =>
  markdown.replace(CITATION_RE, ' $1')

// Inline style so the pill wins over `.simple-rich-text a` on specificity;
// hover is driven from state since `style` can't express `:hover`.
const refPillStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 16,
  height: 16,
  padding: '0 4px',
  transform: `translateY(3px)`,
  borderRadius: 5,
  fontSize: 10,
  fontWeight: 800,
  lineHeight: 1,
  verticalAlign: 'super',
  textDecoration: 'none',
  color: 'var(--saturn-orange)',
  background: 'var(--moon-dust)',
  transition: 'background 0.15s ease, color 0.15s ease',
}

function ReferencePill({ children, ...props }: React.ComponentProps<'a'>) {
  const [hovered, setHovered] = React.useState(false)
  return (
    <a
      {...props}
      style={{
        ...refPillStyle,
        ...(hovered && {
          background: 'var(--moon-dust-darker)',
          color: 'var(--shade-00)',
        }),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </a>
  )
}

export function AskAnswer({
  markdown,
  sources,
  onNavigate,
}: {
  markdown: string
  sources: HelpAskSource[]
  /** Called when a source link is followed, so the host can collapse the chat. */
  onNavigate?: () => void
}) {
  const routes = useRoutes()

  // Sources in the help tree link to their /help article — a plain same-origin
  // anchor that vike's client router picks up, so navigation keeps the widget
  // (and the rest of the layout) mounted. Anything else opens its canonical
  // url in a new tab.
  const hrefFor = React.useCallback(
    (source: HelpAskSource) =>
      source.noteId
        ? routes.helpCenter.article(
            helpArticlePathParams({ id: source.noteId, title: source.title })
          )
        : source.urls[0],
    [routes]
  )

  const linkProps = React.useCallback(
    (source: HelpAskSource) =>
      source.noteId
        ? {
            href: hrefFor(source),
            onClick: (event: React.MouseEvent) => {
              const opensNewTab =
                event.metaKey || event.ctrlKey || event.shiftKey
              if (!opensNewTab) {
                onNavigate?.()
              }
            },
          }
        : { href: hrefFor(source), target: '_blank', rel: 'noreferrer' },
    [hrefFor, onNavigate]
  )

  // Citations are matched to their source by url — each `[N](url)` link
  // carries the exact url the source came with.
  const sourcesByUrl = React.useMemo(() => {
    const map = new Map<string, HelpAskSource>()
    for (const source of sources) {
      for (const url of source.urls) {
        map.set(url, source)
      }
    }
    return map
  }, [sources])

  // A pill's number is the source's position in the list below, so an inline
  // citation always points at the matching numbered chip.
  const numberFor = React.useCallback(
    (source: HelpAskSource) => sources.indexOf(source) + 1,
    [sources]
  )

  const components = React.useMemo<Components>(
    () => ({
      a({ href, children }) {
        const label = Array.isArray(children) ? children.join('') : children
        const isReference = typeof label === 'string' && /^\d+$/.test(label)
        const source = href ? sourcesByUrl.get(href) : undefined
        if (isReference && source) {
          return (
            <ReferencePill {...linkProps(source)}>
              {numberFor(source)}
            </ReferencePill>
          )
        }
        return (
          <a href={href} target="_blank" rel="noreferrer">
            {children}
          </a>
        )
      },
    }),
    [sourcesByUrl, linkProps, numberFor]
  )

  return (
    <>
      <Markdown size="small" components={components}>
        {unwrapCitations(markdown)}
      </Markdown>
      {sources.length > 0 && (
        <div className="margin-top-20">
          <div className="uppercase font-weight-bold shade-30 font-size-8 margin-bottom-8">
            <FormattedMessage
              id="website.helpCenter.ask.sourcesCount"
              defaultMessage="{count, plural, one {# source} other {# sources}}"
              values={{ count: sources.length }}
            />
          </div>
          <div className="flexbox flexbox-wrap flexbox-gap-8">
            {sources.map((source) => (
              <a
                key={source.noteId ?? source.urls[0]}
                {...linkProps(source)}
                className="flexbox flexbox-align-center flexbox-gap-4 max-width-full radius-large padding-vertical-2 padding-horizontal-8 shade-10 font-weight-semibold background-alpha-shade-55 background-alpha-shade-50-on-hover"
                css={{ textDecoration: 'none' }}
              >
                <CustomNoteIcon
                  iconShape={source.iconShape}
                  iconColor={source.iconColor}
                  size={14}
                  className="shade-20 flexbox-no-shrink"
                />
                <span className="ellipsis font-size-7" css={{ maxWidth: 220 }}>
                  {source.title}
                </span>
                <ReferencePill {...linkProps(source)}>
                  {numberFor(source)}
                </ReferencePill>
              </a>
            ))}
          </div>
        </div>
      )}
    </>
  )
}
